import { watchEffect } from 'vue';

import { storeToRefs } from 'pinia';

import { isHiddenMarketplaceType } from '@/helpers/marketplaceUnion.js';
import navigation from '@/navigation/vertical';
import { isDevOnlyPathAllowed } from '@/router/devOnlyPaths.js';
import { useAccountStore } from '@/stores/account.js';
import { useAuthStore } from '@/stores/auth.js';
import { isReadableEbayAccount, useEbayAccountStore } from '@/stores/ebayAccount.js';
import { useEnvironmentStore } from '@/stores/environment.js';
import { isReadableMetaAdAccount, useMetaAdAccountStore } from '@/stores/metaAdAccount.js';
import { isReadableShopifyLink, useShopifyLinkStore } from '@/stores/shopifyLink.js';
import { isReadableWalmartAccount, useWalmartAccountStore } from '@/stores/walmartAccount.js';


// Which marketplaces are connected for the CURRENT business profile, so the sidebar can
// show a marketplace group only when it has something to show. A nav item opts in with
// `channel: '<key>'`; `isChannelAllowed` (useNavItemVisibility) turns that into a gate.
//
// Channel keys:
// - `amazon-ads`    the profile has at least one Ads profile (profileId set)
// - `amazon-seller` the profile has at least one non-SHADOW SP marketplace
// - `amazon`        either of the two
// - `ebay`          an ACTIVE eBay connection (loaded on demand)
// - `shopify`       an ACTIVE Shopify link (loaded on demand)
// - `walmart`       an ACTIVE Walmart account (loaded on demand)
// - `meta`          a readable Meta ad account (ACTIVE connection, ticked ad account; loaded on demand)
// Any other key (a future marketplace) reads as not connected, and ready — so a group
// tagged with an unknown channel simply stays hidden instead of waiting forever.
//
// `isConnected` / `isChannelReady` are PURE reads, safe in a template v-if. Fetching is
// separate: call `startLoading()` once from component setup. By default the eBay / Shopify /
// Walmart / Meta connections are fetched only while some nav item carrying that channel passes
// `isEnvAllowed`; `startLoading({ ignoreGating: true })` fetches them all regardless (the
// Overview and the connect bar do, so a build that hides those groups still makes the calls).
// A channel whose pages are not allowed in this environment for this user (see CHANNEL_GATE_PATHS)
// is never fetched, even with `ignoreGating`: its API is off there and answers 404. It reads as
// loaded and not connected, so the Overview skeleton and the connect banner still settle.
// Must be called from component setup (or a render): the channel stores resolve useRoute().
// `isEnvAllowed` is passed in rather than imported — useNavItemVisibility builds this
// composable lazily for `isChannelAllowed`, and a direct import would be a cycle.

const LOADABLE_CHANNELS = ['ebay', 'shopify', 'walmart', 'meta'];

// Every channel whose connection state is held globally (the account store's business profile, or
// a channel store above): "has this business connected anything?" counts all of them, whether or
// not their nav group is visible on this build. Google, TikTok, Pinterest and Bing are NOT here:
// their connections are only fetched on their own pages (no global store), so they cannot be
// counted without a new request per page load.
export const ANY_CONNECTION_CHANNELS = Object.freeze([
  'amazon-ads',
  'amazon-seller',
  ...LOADABLE_CHANNELS,
]);

// The integration page that decides whether a channel can exist for this user: its connections
// route is off wherever the page is dev-only (`isDevOnlyPathAllowed`: false for a regular user on
// stage / prod, true on dev and for a super admin who lifted the gate). eBay is deliberately absent:
// it is live on prod, so its connections are always fetched.
const CHANNEL_GATE_PATHS = Object.freeze({
  shopify: '/integrations/shopify',
  walmart: '/integrations/walmart',
  meta: '/integrations/meta',
});

const collectChannelItems = (items, found = []) => {
  for (const item of items) {
    if (item.channel) found.push(item);
    if (item.children) collectChannelItems(item.children, found);
  }
  return found;
};

const CHANNEL_STORES = {
  ebay: useEbayAccountStore,
  shopify: useShopifyLinkStore,
  walmart: useWalmartAccountStore,
  meta: useMetaAdAccountStore,
};

// Re-fetch one marketplace's connections so the sidebar follows a connect / disconnect /
// delete made on its integration page. The channel stores load once per business profile
// and cache the answer (`ensureLoaded`), so without this the nav keeps the old answer until
// a full reload. `isLoaded` stays true during the refetch, so the group does not flicker.
// Fire-and-forget: the store's `load` never rejects. Needs an active Pinia (component
// setup or an event handler); a key that is not loadable (the Amazon ones) is a no-op.
export const refreshConnectedChannel = (channel) => {
  const useStore = CHANNEL_STORES[channel];
  return useStore ? useStore().load() : Promise.resolve();
};

const channelItemsCache = new WeakMap();
const channelItemsOf = (navItems) => {
  if (!channelItemsCache.has(navItems)) channelItemsCache.set(navItems, collectChannelItems(navItems));
  return channelItemsCache.get(navItems);
};

export const useConnectedChannels = ({ isEnvAllowed, navItems = navigation }) => {
  const account = useAccountStore();
  const { currentBusinessProfile } = storeToRefs(account);
  const ebay = useEbayAccountStore();
  const shopify = useShopifyLinkStore();
  const walmart = useWalmartAccountStore();
  const meta = useMetaAdAccountStore();
  const authStore = useAuthStore();
  const envStore = useEnvironmentStore();

  const channelItems = channelItemsOf(navItems);

  const hasAds = () => account.extractMarketplacesFromProfile(currentBusinessProfile.value).length > 0;

  const hasSeller = () =>
    (currentBusinessProfile.value?.amazonSPAccounts || []).some((spAccount) =>
      (spAccount.marketplaces || []).some((mp) => mp.marketplaceId && !isHiddenMarketplaceType(mp)),
    );

  const channelStores = { ebay, shopify, walmart, meta };

  const connectionCheckers = {
    'amazon-ads': hasAds,
    'amazon-seller': hasSeller,
    amazon: () => hasAds() || hasSeller(),
    ebay: () => ebay.accounts.some(isReadableEbayAccount),
    shopify: () => shopify.accounts.some(isReadableShopifyLink),
    walmart: () => walmart.accounts.some(isReadableWalmartAccount),
    meta: () => meta.accounts.some(isReadableMetaAdAccount),
  };

  // False for a channel this user's environment hides: it is not requested and counts as loaded.
  const isChannelFetchable = (channel) => {
    const path = CHANNEL_GATE_PATHS[channel];
    return !path || isDevOnlyPathAllowed(path, authStore, envStore);
  };

  const isChannelLoaded = (channel) => !isChannelFetchable(channel) || channelStores[channel].isLoaded;

  const readinessCheckers = {
    'amazon-ads': () => !!currentBusinessProfile.value,
    'amazon-seller': () => !!currentBusinessProfile.value,
    amazon: () => !!currentBusinessProfile.value,
    ebay: () => isChannelLoaded('ebay'),
    shopify: () => isChannelLoaded('shopify'),
    walmart: () => isChannelLoaded('walmart'),
    meta: () => isChannelLoaded('meta'),
  };

  const isConnected = (channel) => connectionCheckers[channel]?.() ?? false;
  const isChannelReady = (channel) => readinessCheckers[channel]?.() ?? true;

  const isChannelShown = (channel) =>
    channelItems.some((item) => item.channel === channel && isEnvAllowed(item));

  // Reads `isLoaded` (which depends on the current BP id), so the effect re-runs after
  // the store resets on a business-profile switch and loads the new profile's connections.
  // `ignoreGating` loads every loadable channel, for a caller that must know whether the business
  // has connected anything at all (the connect bar, the Overview empty state).
  const startLoading = ({ ignoreGating = false } = {}) =>
    watchEffect(() => {
      if (!currentBusinessProfile.value?.id) return;
      for (const channel of LOADABLE_CHANNELS) {
        const store = channelStores[channel];
        if (store.isLoaded || !isChannelFetchable(channel)) continue;
        if (!ignoreGating && !isChannelShown(channel)) continue;
        store.ensureLoaded();
      }
    });

  // [{ ready, connected }] for every channel in ANY_CONNECTION_CHANNELS, gated or not.
  const anyConnectionStates = () =>
    ANY_CONNECTION_CHANNELS.map((channel) => ({
      ready: isChannelReady(channel),
      connected: isConnected(channel),
    }));

  return {
    isConnected,
    isChannelReady,
    isChannelShown,
    startLoading,
    anyConnectionStates,
  };
};
